import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Pencil, Trash2, Building2 } from "lucide-react";

const inputCls = "mt-1.5 rounded-none bg-background border-border h-9";

export default function CompaniesPage() {
  const { canEdit } = useAuth();
  const editable = canEdit("vehicles");
  const [companies, setCompanies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", notes: "", vehicle_ids: [] });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditId(null); setForm({ name: "", notes: "", vehicle_ids: [] }); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ name: c.name, notes: c.notes || "", vehicle_ids: c.vehicle_ids || [] }); setOpen(true); };

  const toggleVehicle = (id) =>
    setForm((f) => ({ ...f, vehicle_ids: f.vehicle_ids.includes(id) ? f.vehicle_ids.filter((x) => x !== id) : [...f.vehicle_ids, id] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      if (editId) await api.put(`/companies/${editId}`, form);
      else await api.post("/companies", form);
      toast.success(editId ? "Company updated" : `Company ${form.name} added`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete company ${c.name}? Its vehicles will be unassigned.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Company deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-8" data-testid="companies-page">
      <Link to="/vehicles" data-testid="back-to-vehicles-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Vehicles
      </Link>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">COMPANIES</h1>
          <p className="text-muted-foreground text-sm mt-1">{companies.length} companies — assign which vehicles belong to which company</p>
        </div>
        {editable && (
          <Button data-testid="add-company-button" onClick={openAdd} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
            <Plus size={15} className="mr-1" /> Add company
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-none text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">{editId ? "EDIT COMPANY" : "ADD COMPANY"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Company name *</Label>
              <Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Notes</Label>
              <Input data-testid="company-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
            </div>
            {vehicles.length > 0 && (
              <div>
                <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Vehicles in this company ({form.vehicle_ids.length} selected)</Label>
                <div className="mt-1.5 border border-border max-h-52 overflow-y-auto grid grid-cols-2" data-testid="company-vehicles-picker">
                  {vehicles.map((v) => (
                    <label key={v.id} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-muted/60">
                      <input
                        type="checkbox"
                        data-testid={`company-vehicle-${v.registration}`}
                        checked={form.vehicle_ids.includes(v.id)}
                        onChange={() => toggleVehicle(v.id)}
                        className="accent-[#FF3B30]"
                      />
                      <span className="font-mono font-bold">{v.registration}</span>
                      {v.company_id && v.company_id !== editId && (
                        <span className="text-amber-600 dark:text-[#FFD60A] truncate" title={`Currently in ${v.company_name}`}>({v.company_name})</span>
                      )}
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">A vehicle belongs to one company — ticking it here moves it out of its current company.</p>
              </div>
            )}
            <Button data-testid="save-company-button" onClick={save} disabled={saving} className="w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">
              {saving ? "Saving…" : editId ? "Save changes" : "Create company"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="companies-table">
          <thead>
            <tr className="text-left border-b border-border">
              {["Company", "Vehicles", "Notes", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.name}`} className="border-b border-border/60 hover:bg-muted/60">
                <td className="px-4 py-3 font-semibold whitespace-nowrap">
                  <span className="inline-flex items-center gap-2"><Building2 size={15} className="text-muted-foreground" /> {c.name}</span>
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`company-vehicles-${c.name}`}>
                  {c.vehicle_regs?.length ? (
                    <div className="max-w-[340px]">
                      <span className="font-bold">{c.vehicle_regs.length} vehicle{c.vehicle_regs.length === 1 ? "" : "s"}</span>
                      <div className="text-muted-foreground font-mono truncate" title={c.vehicle_regs.join(", ")}>
                        {c.vehicle_regs.slice(0, 5).join(", ")}{c.vehicle_regs.length > 5 ? ` +${c.vehicle_regs.length - 5}` : ""}
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground/60">No vehicles</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{c.notes || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {editable && (
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-company-${c.name}`} onClick={() => openEdit(c)} className="text-muted-foreground/70 hover:text-foreground transition-colors duration-150"><Pencil size={14} /></button>
                      <button data-testid={`delete-company-${c.name}`} onClick={() => remove(c)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150"><Trash2 size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {companies.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground/70">No companies yet. Add a company and tick which vehicles belong to it.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
